/**
 * Class SimpleShowMore allows revealing hidden content dynamically across separate components, linked by identifiers rather than DOM hierarchy.
 * This approach enables the button and container to be located separately in the markup.
 *
 * How it works:
 * - Each component (either a container or a button) is identified by a unique ID.
 * - The class expects:
 *   1. A container identified by `data-simple-show-more-container="some_id"` which holds content, some hidden with the `hidden` CSS class.
 *   2. A button identified by `data-simple-show-more-button="some_id"` used to reveal content in the container.
 * - When the button is clicked, it triggers revealing up to the specified number of hidden elements in the associated container.
 * - If there are no more hidden elements to reveal, the button is hidden to prevent further clicks.
 *
 * Usage:
 * - To initialize the show more functionality, ensure all elements are marked with corresponding data attributes and call `SimpleShowMore.init()` after the document content has loaded.
 */
export default class SimpleShowMore {
  constructor(container, button, count, hiddenClass) {
    this.showMoreContainer = container;
    this.showMoreButton = button;
    this.showMoreCount = parseInt(count, 10);
    this.hiddenClass = hiddenClass;

    this.showMoreButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.showMore();
    });
  }

  showMore() {
    let hiddenElements = this.showMoreContainer.querySelectorAll(`.${this.hiddenClass}`);
    let elementsToShow = Math.min(hiddenElements.length, this.showMoreCount);
    for (let i = 0; i < elementsToShow; i++) {
      hiddenElements[i].classList.remove(this.hiddenClass);
    }
    if (hiddenElements.length <= elementsToShow) {
      this.showMoreButton.style.display = "none";
    }
  }

  static init() {
    const containers = document.querySelectorAll(
      "[data-simple-show-more-container]"
    );
    containers.forEach((container) => {
      const id = container.getAttribute("data-simple-show-more-container");
      const button = document.querySelector(
        `[data-simple-show-more-button="${id}"]`
      );
      const count = container.getAttribute("data-simple-show-more-count") || 10;
      const hiddenClass =
        container.getAttribute("data-simple-show-more-hidden-class") ||
        "hidden";

      if (container && button) {
        new SimpleShowMore(container, button, count, hiddenClass);
      }
    });
  }
}
